import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [isNavOpen, setIsNavOpen] = useState(false);
    const navbarCollapseRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        
        // Close navbar when route changes
        setIsNavOpen(false);
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [location]);

    // Close navbar on any click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isNavOpen && 
                navbarCollapseRef.current && 
                !navbarCollapseRef.current.contains(event.target) &&
                !event.target.closest('.navbar-toggler')) {
                setIsNavOpen(false);
            }
        };

        if (isNavOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isNavOpen]);

    const handleNavToggle = () => {
        setIsNavOpen(!isNavOpen);
    };

    const handleNavLinkClick = () => {
        // Close navbar on mobile when a link is clicked
        if (window.innerWidth < 992) {
            setIsNavOpen(false);
        }
    };

    const navItems = [
        { path: '/', label: 'Home' },
        { path: '/about', label: 'About Us' },
        { path: '/services', label: 'Services' },
        { path: '/projects', label: 'Projects' },
        { path: '/blog', label: 'Blog' },
        { path: '/contact', label: 'Contact' },
    ];

    return (
        <nav className={`navbar navbar-expand-lg fixed-top ${scrolled ? 'scrolled' : ''}`}>
            <div className="container">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <NavLink className="navbar-brand" to="/" onClick={handleNavLinkClick}>
                        <div className="d-flex align-items-center">
                            {/* Logo Image */}
                            <img
                                src="/assets/images/logo.png"
                                alt="LAMSAT Technical Services"
                                className="brand-logo me-2"
                                style={{
                                    height: '50px',
                                    width: 'auto',
                                    objectFit: 'contain'
                                }}
                                onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/150x50/2E2F5B/ffffff?text=LAMSAT';
                                }}
                            />
                            {/* Brand Text */}
                            <div className="brand-text">
                                <h3 className="mb-0 text-white" style={{ fontFamily: 'Poppins', fontWeight: 700 }}>
                                    LAMSAT
                                    <span className="text-primary-red" style={{ fontSize: '0.8em' }}>™</span>
                                </h3>
                                <p className="mb-0" style={{ fontSize: '0.8rem', color: '#ccc' }}>
                                    Technical Services L.L.C
                                </p>
                            </div>
                        </div>
                    </NavLink>
                </motion.div>

                <button
                    className="navbar-toggler"
                    type="button"
                    onClick={handleNavToggle}
                    aria-expanded={isNavOpen}
                    aria-label="Toggle navigation"
                    aria-controls="navbarNav"
                >
                    <span className="navbar-toggler-icon text-white">
                        <i className={`fas ${isNavOpen ? 'fa-times' : 'fa-bars'}`}></i>
                    </span>
                </button>

                <div 
                    ref={navbarCollapseRef}
                    className={`collapse navbar-collapse ${isNavOpen ? 'show' : ''}`}
                    id="navbarNav"
                >
                    <ul className="navbar-nav ms-auto">
                        {navItems.map((item, index) => (
                            <motion.li
                                key={item.path}
                                className="nav-item"
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <NavLink
                                    className={({ isActive }) =>
                                        `nav-link ${isActive ? 'active' : ''}`
                                    }
                                    to={item.path}
                                    onClick={handleNavLinkClick}
                                >
                                    {item.label}
                                </NavLink>
                            </motion.li>
                        ))}
                        <motion.li
                            className="nav-item ms-lg-3"
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.6 }}
                        >
                            <NavLink 
                                to="/contact" 
                                className="btn btn-primary-custom mt-2 mt-lg-0"
                                onClick={handleNavLinkClick}
                            >
                                <i className="fas fa-envelope me-2"></i>
                                Get Quote
                            </NavLink>
                        </motion.li>
                    </ul>
                </div>
            </div>

            {/* Add custom CSS for better mobile experience */}
            <style jsx>{`
                .navbar {
                    background-color: var(--primary-blue);
                    transition: all 0.3s ease;
                    padding: 0.5rem 0;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                }
                
                .navbar.scrolled {
                    padding: 0.3rem 0;
                    background-color: rgba(46, 47, 91, 0.95);
                    backdrop-filter: blur(10px);
                }
                
                @media (max-width: 991.98px) {
                    .navbar-collapse {
                        position: absolute;
                        top: 100%;
                        left: 0;
                        right: 0;
                        background-color: var(--primary-blue);
                        z-index: 1000;
                        padding: 1rem;
                        box-shadow: 0 5px 15px rgba(0,0,0,0.2);
                        max-height: calc(100vh - 70px);
                        overflow-y: auto;
                        transition: all 0.3s ease-in-out;
                    }
                    
                    .navbar-collapse.collapse:not(.show) {
                        display: none;
                    }
                    
                    .navbar-collapse.show {
                        display: block;
                        animation: slideDown 0.3s ease-in-out;
                    }
                    
                    @keyframes slideDown {
                        from {
                            opacity: 0;
                            transform: translateY(-10px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    
                    .navbar-nav {
                        text-align: center;
                    }
                    
                    .nav-item {
                        margin: 0.5rem 0;
                    }
                    
                    .nav-link {
                        font-size: 1.1rem;
                        padding: 0.75rem 1rem;
                        color: white !important;
                        transition: all 0.3s ease;
                    }
                    
                    .nav-link:hover,
                    .nav-link.active {
                        color: var(--primary-red) !important;
                        background-color: rgba(255, 255, 255, 0.1);
                        border-radius: 4px;
                    }
                    
                    .btn-primary-custom {
                        width: 100%;
                        max-width: 200px;
                        margin: 1rem auto;
                        display: block;
                    }
                    
                    .navbar-toggler {
                        border: none;
                        padding: 0.5rem;
                        background: transparent;
                    }
                    
                    .navbar-toggler:focus {
                        box-shadow: none;
                        outline: none;
                    }
                    
                    .navbar-toggler-icon {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 30px;
                        height: 30px;
                    }
                    
                    .navbar-toggler-icon i {
                        font-size: 1.5rem;
                        transition: transform 0.3s ease;
                    }
                }
                
                @media (min-width: 992px) {
                    .navbar-collapse {
                        position: static;
                        background-color: transparent;
                        box-shadow: none;
                        padding: 0;
                        display: flex !important;
                    }
                    
                    .nav-link {
                        color: white !important;
                        margin: 0 0.5rem;
                        padding: 0.5rem 1rem !important;
                    }
                    
                    .nav-link:hover,
                    .nav-link.active {
                        color: var(--primary-red) !important;
                    }
                    
                    .btn-primary-custom {
                        margin-left: 1rem;
                    }
                }
                
                /* Logo styles */
                .brand-logo {
                    transition: all 0.3s ease;
                }
                
                .navbar.scrolled .brand-logo {
                    height: 40px !important;
                }
                
                .brand-text h3 {
                    transition: all 0.3s ease;
                }
                
                .navbar.scrolled .brand-text h3 {
                    font-size: 1.5rem !important;
                }
                
                .navbar.scrolled .brand-text p {
                    font-size: 0.7rem !important;
                }
            `}</style>
        </nav>
    );
};

export default Header;